import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EllipsisVerticalIcon, FileDownIcon, PanelRightCloseIcon, PanelRightOpenIcon, PencilIcon, Trash2Icon } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { toast } from "sonner";
import type { SessionDetail, SessionSummary, TurnStatus } from "../../../../shared/api.ts";
import { NavigationButton } from "@/components/shell/shell-context.tsx";
import { DeleteSessionDialog, RenameSessionDialog } from "@/components/shell/session-dialogs.tsx";
import { Badge, type BadgeProps } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu.tsx";
import { Tooltip } from "@/components/ui/tooltip.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { errorMessage } from "@/lib/hooks.ts";
import { isMockTransport } from "@/lib/http.ts";
import { sessionStatus, type SessionStatus } from "@/lib/turns.ts";
import { cn } from "@/lib/utils.ts";

const STATUS_VARIANT: Record<SessionStatus, NonNullable<BadgeProps["variant"]>> = {
  running: "primary",
  classified: "success",
  clarifying: "warning",
  rejected: "neutral",
  new: "neutral",
};

export function SessionStatusBadge({
  session,
  lastTurnStatus,
  className,
}: {
  session: Pick<SessionSummary, "running" | "phase">;
  /** Shown instead of "New" when the only attempt so far stopped. */
  lastTurnStatus?: TurnStatus | null;
  className?: string;
}) {
  const { t } = useI18n();
  const status = sessionStatus(session);
  if (status === "new" && lastTurnStatus && ["failed", "interrupted", "cancelled"].includes(lastTurnStatus)) {
    return (
      <Badge variant={lastTurnStatus === "failed" ? "danger" : lastTurnStatus === "interrupted" ? "warning" : "neutral"} className={className}>
        {t.turnStatus[lastTurnStatus]}
      </Badge>
    );
  }
  const label = {
    running: t.session.statusRunning,
    classified: t.session.statusClassified,
    clarifying: t.session.statusClarifying,
    rejected: t.session.statusRejected,
    new: t.session.statusNew,
  }[status];
  return (
    <Badge variant={STATUS_VARIANT[status]} className={className}>
      {status === "running" ? <span className="size-1.5 animate-pulse rounded-full bg-current" aria-hidden /> : null}
      {label}
    </Badge>
  );
}

/** An editable title: click to rename, Enter or blur saves, Escape cancels. */
function InlineTitle({ session, readOnly }: { session: SessionDetail["session"]; readOnly: boolean }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(session.title);
  const inputRef = useRef<HTMLInputElement>(null);
  const title = session.title || t.shell.untitled;

  useEffect(() => {
    if (!editing) setValue(session.title);
  }, [session.title, editing]);
  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const rename = useMutation({
    mutationFn: (next: string) => api.renameSession(session.id, { title: next }),
    onMutate: (next) => {
      queryClient.setQueryData<SessionDetail>(queryKeys.session(session.id), (detail) =>
        detail ? { ...detail, session: { ...detail.session, title: next } } : detail,
      );
      queryClient.setQueryData<SessionSummary[]>(queryKeys.sessions, (list) => list?.map((item) => (item.id === session.id ? { ...item, title: next } : item)));
    },
    onError: (error) => {
      toast.error(errorMessage(error, t));
      void queryClient.invalidateQueries({ queryKey: queryKeys.session(session.id) });
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: queryKeys.sessions }),
  });

  const commit = () => {
    const next = value.trim();
    setEditing(false);
    if (next && next !== session.title) rename.mutate(next);
  };

  if (readOnly) {
    return <h1 className="min-w-0 truncate text-[15px] font-bold tracking-[-0.01em]">{title}</h1>;
  }
  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        aria-label={t.shell.renameLabel}
        maxLength={200}
        onChange={(event) => setValue(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
          if (event.key === "Escape") {
            setValue(session.title);
            setEditing(false);
          }
        }}
        className="h-8 min-w-0 flex-1 rounded-md border border-ring bg-card px-2 text-[15px] font-bold outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25"
      />
    );
  }
  return (
    <Tooltip content={t.session.renameHint}>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="group flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-left outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/35"
      >
        <h1 className="min-w-0 truncate text-[15px] font-bold tracking-[-0.01em]">{title}</h1>
        <PencilIcon className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden />
      </button>
    </Tooltip>
  );
}

/** Title (click to rename), status badge, Export PDF, Delete, and the right-panel toggle. */
export function SessionHeader({
  detail,
  readOnly,
  panelOpen,
  onTogglePanel,
  showPanelToggle,
  leading,
}: {
  detail: SessionDetail;
  readOnly: boolean;
  panelOpen: boolean;
  onTogglePanel: () => void;
  showPanelToggle: boolean;
  leading?: ReactNode;
}) {
  const { t, language } = useI18n();
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const session = detail.session;

  const exportPdf = () => {
    if (isMockTransport()) {
      toast.message(t.session.exportUnavailable);
      return;
    }
    window.open(api.exportPdfUrl(session.id, language), "_blank", "noopener");
  };

  return (
    <header className="flex h-14 shrink-0 items-center gap-1.5 border-b bg-background px-2.5 sm:gap-2 sm:px-4">
      {leading ?? <NavigationButton />}
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <InlineTitle session={session} readOnly={readOnly} />
        <SessionStatusBadge
          session={session}
          lastTurnStatus={detail.turns[detail.turns.length - 1]?.status ?? null}
          className="hidden shrink-0 sm:inline-flex"
        />
      </div>
      <div className="flex shrink-0 items-center gap-0.5">
        {!readOnly ? (
          <>
            <Tooltip content={t.session.exportPdf}>
              <Button variant="ghost-muted" size="sm" className="hidden md:inline-flex" onClick={exportPdf}>
                <FileDownIcon aria-hidden />
                <span className="hidden lg:inline">{t.session.exportPdf}</span>
                <span className="sr-only lg:hidden">{t.session.exportPdf}</span>
              </Button>
            </Tooltip>
            <Tooltip content={t.common.delete}>
              <Button variant="ghost-muted" size="icon-sm" className="hidden hover:text-danger md:inline-flex" aria-label={t.common.delete} onClick={() => setDeleting(true)}>
                <Trash2Icon />
              </Button>
            </Tooltip>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost-muted" size="icon-sm" className="md:hidden" aria-label={t.session.actions}>
                  <EllipsisVerticalIcon />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setRenaming(true)}>
                  <PencilIcon aria-hidden />
                  {t.common.rename}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={exportPdf}>
                  <FileDownIcon aria-hidden />
                  {t.session.exportPdf}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="danger" onSelect={() => setDeleting(true)}>
                  <Trash2Icon aria-hidden />
                  {t.common.delete}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : null}
        {showPanelToggle ? (
          <Tooltip content={panelOpen ? t.session.hidePanel : t.session.showPanel}>
            <Button
              variant="ghost-muted"
              size="icon-sm"
              aria-label={panelOpen ? t.session.hidePanel : t.session.showPanel}
              aria-pressed={panelOpen}
              onClick={onTogglePanel}
              className={cn(panelOpen && "text-foreground")}
            >
              {panelOpen ? <PanelRightCloseIcon /> : <PanelRightOpenIcon />}
            </Button>
          </Tooltip>
        ) : null}
      </div>
      {!readOnly ? (
        <>
          <DeleteSessionDialog session={session} open={deleting} onOpenChange={setDeleting} />
          <RenameSessionDialog session={session} open={renaming} onOpenChange={setRenaming} />
        </>
      ) : null}
    </header>
  );
}
